import { IncidentHistoryView } from '../view/IncidentHistoryView';
import { templateIncidentHistory } from '../view/template';
import { IncidentHistoryRepository } from '../repository/IncidentHistoryRepository';
import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { IncidentHistoryWebComponent } from '../index';
import { IncidentHistoryWebComponentSchema } from '../types';

export class IncidentHistoryController extends BaseWebComponentController {
  public component: IncidentHistoryWebComponent;
  public dataRepository: IncidentHistoryRepository;
  public view: IncidentHistoryView;
  public viewConstructor: typeof IncidentHistoryView;
  public dataRepositoryConstructor: typeof IncidentHistoryRepository;

  private page = 1;
  private totalNumberOfIncidentsHistory = 0;
  private totalNumberOfIncidentsHistoryDisplayed = 0;
  private anotherPageIsLoading = false;
  private allResultsLoaded = false;

  constructor(component: IncidentHistoryWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: IncidentHistoryView,
      viewTemplate: templateIncidentHistory,
      dataRepositoryConstructor: IncidentHistoryRepository,
    });
  }

  async getDataForView(): Promise<{ incidentsHistory: Array<IncidentHistoryWebComponentSchema> }> {
    this.view.setLoadingState();
    try {
      const results = await this.dataRepository.getDataForView(this.page);

      this.totalNumberOfIncidentsHistory = results.incidentsHistoryResults.total;
      this.totalNumberOfIncidentsHistoryDisplayed += results.incidentsHistoryResults.data.length;

      this.checkAllResultsAreLoaded();

      return {
        incidentsHistory: results.incidentsHistoryResults.data,
      };
    } catch (error) {
      throw error;
    } finally {
      this.view.removeTemplateState();
    }
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$panelSideMenu,
        event: 'close',
        callback: this.onCloseSidePanel,
      },
      {
        element: this.view.dom.$incidentHistoryList,
        event: 'scroll',
        callback: this.handleScrollEvent,
      },
    ];
  }

  private async handleScrollEvent() {
    const listContainer = this.view.dom.$incidentHistoryList;

    if (Math.ceil(Math.abs(listContainer.scrollTop) + listContainer.clientHeight) !== listContainer.scrollHeight) {
      return;
    }

    if (this.anotherPageIsLoading || this.allResultsLoaded) {
      return;
    }

    this.anotherPageIsLoading = true;

    try {
      this.page++;
      this.view.setScrollLoadingState();
      const results = await this.dataRepository.getDataForView(this.page);

      if (results.incidentsHistoryResults.data.length === 0) {
        return;
      }

      this.totalNumberOfIncidentsHistoryDisplayed += results.incidentsHistoryResults.data.length;

      for (const incidentHistory of results.incidentsHistoryResults.data) {
        this.view.addIncidentHistoryCard(incidentHistory);
      }

      this.checkAllResultsAreLoaded();
      this.view.removeTemplateState();
    } catch (error) {
      this.view.buildErrorView(error);
    } finally {
      this.anotherPageIsLoading = false;
    }
  }

  checkAllResultsAreLoaded() {
    if (this.totalNumberOfIncidentsHistoryDisplayed >= this.totalNumberOfIncidentsHistory) {
      this.allResultsLoaded = true;
    }
  }

  private onCloseSidePanel(event: WebComponentEvent) {
    this.component.dispatchEvent(
      new CustomEvent('closePanelSideMenu', {
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }
}
